import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { debounce } from 'lodash';
import { loadMore, setLayoutChanging } from '../../redux/slices/filterSlice';
import LoadingSpinner from '../ui/LoadingSpinner';
// Status Components


export const ErrorMessage = ({ message }) => (
  <div className="bg-red-50 border-l-4 border-red-400 p-4 rounded mb-20">
    <p className="text-red-700 font-medium">Error Loading Products</p>
    <p className="text-red-600">{message || 'Please try again later.'}</p>
  </div>
);

export const NoResultsMessage = ({ searchTerm }) => (
  <div className="bg-brand-beige/10 border-l-4 border-brand-beige p-4 rounded mb-20">
    <p className="text-brand-orange font-medium">No Products Found</p>
    <p className="text-brand-orange-dark">
      {searchTerm 
        ? `No results found for "${searchTerm}". Try different search terms.`
        : 'Try adjusting your filters to see more results.'}
    </p>
  </div>
);

export const EndOfResultsMessage = () => (
  <div className="mt-8 mb-20 bg-brand-beige/10 border-l-4 border-brand-beige p-4 rounded">
    <p className="text-brand-orange font-medium">End of Results</p>
    <p className="text-brand-orange-dark">You've seen all the products that match your criteria.</p>
  </div>
);

const CardsContainer = ({ 
  children,
  className = '',
  loading,
  error,
  hasMore,
  totalResults,
  searchTerm,
  dataLoading,
  isInitialized,
  lastSearch
}) => {
  const dispatch = useDispatch();
  const layoutChanging = useSelector(state => state.filter.layoutChanging);
  const pageType = useSelector(state => state.filter.pageType);
  const scrollContainerRef = useRef(null);
  const isLoadingRef = useRef(false);
  const [isResizing, setIsResizing] = useState(false);
  const resizeTimerRef = useRef(null);
  const childrenArray = React.Children.toArray(children);
  // Reset state when component mounts/unmounts or page type changes
  useEffect(() => {
    // Reset the layout changing flag when component mounts

    dispatch(setLayoutChanging(false));
    
    return () => {
      // Clean up when unmounting
      if (resizeTimerRef.current) {
        clearTimeout(resizeTimerRef.current);
      }
      
      // Make sure to reset layout changing flag on unmount
      dispatch(setLayoutChanging(false));
    };
  }, [dispatch, pageType]);

  // Detect window resize
  useEffect(() => {
    const handleResize = () => {
      setIsResizing(true);
      
      // Also set the global layout changing flag
      dispatch(setLayoutChanging(true));
      
      // Clear any existing timer
      if (resizeTimerRef.current) {
        clearTimeout(resizeTimerRef.current);
      }
      
      // Set a new timer with a longer timeout
      resizeTimerRef.current = setTimeout(() => {
        setIsResizing(false);
        dispatch(setLayoutChanging(false));
      }, 1000);
    };
    
    window.addEventListener('resize', handleResize);
    
    return () => {
      window.removeEventListener('resize', handleResize);
      if (resizeTimerRef.current) {
        clearTimeout(resizeTimerRef.current);
        dispatch(setLayoutChanging(false));
      }
    };
  }, [dispatch]);

  // Setup scroll container and handlers
  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    const handleScroll = () => {
      // Skip scroll processing completely during layout changes or if component is unmounting
      if (isResizing || layoutChanging) {
        return;
      }
      
      const scrollPosition = Math.ceil(container.scrollTop + container.clientHeight);
      const scrollThreshold = container.scrollHeight - 100;
      
      if (scrollPosition >= scrollThreshold && !loading && hasMore && !isLoadingRef.current && !isResizing && !layoutChanging) {
        isLoadingRef.current = true;
        
        dispatch(loadMore());
        
        // Set a small timeout to prevent rapid fire loading events
        setTimeout(() => {
          isLoadingRef.current = false;
        }, 500);
      }
    };

    // Increase debounce time to reduce frequency of checks
    const debouncedScroll = debounce(handleScroll, 200);
    container.addEventListener('scroll', debouncedScroll);
    
    return () => {
      container.removeEventListener('scroll', debouncedScroll);
      debouncedScroll.cancel();
    };
  }, [hasMore, loading, dispatch, isResizing, layoutChanging]);

  const renderContent = () => {
    // Show loading spinner during initial load or explicit loading
    if (loading || (!isInitialized && !lastSearch)) {
      return <LoadingSpinner />;
    }

    // Show error if we have one and no products
    if (error && !loading && childrenArray.length === 0) {
      return <ErrorMessage message={error} />;
    }

    // Show products grid if we have products
    if (children) {
      return (
        <div className="px-4 py-3">
          {searchTerm && totalResults > 0 && (
            <div className="mb-3 text-sm text-gray-600">
              Found {totalResults} results for "{searchTerm}"
            </div>
          )}
          
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 2xl:grid-cols-6 gap-4">
            {children}
          </div>
          
          {/* Show loading spinner at bottom during pagination */}
          {dataLoading && <LoadingSpinner />}
          {isInitialized && lastSearch &&!hasMore && !loading && !dataLoading && <EndOfResultsMessage />}
        </div>
      );
    }

    // Only show no results if we're initialized and have made at least one search
    if (isInitialized && lastSearch && !loading && !dataLoading) {
      return <NoResultsMessage searchTerm={searchTerm} />;
    }

    // Fallback loading state
    return <LoadingSpinner />;
  };

  return (
    <div 
      ref={scrollContainerRef}
      className={`overflow-y-auto ${className}`}
      style={{ maxHeight: 'calc(100vh - 100px)' }}
    >
      {renderContent()}
    </div>
  );
};

export default CardsContainer; 